import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { supplyPickupApi } from "../../services/supplyPickupApi";
import { apiClient, formatApiErrorDetail } from "../../lib/api";
import { usePermissions } from "../../hooks/usePermissions";
import { useRouteRefresh } from "../../hooks/useRouteRefresh";
import { PageHelpBanner } from "../../components/PageHelpBanner";
import { StatusBadge } from "../../components/StatusBadge";
import { SupplyPickupAssignmentDetail } from "../../components/SupplyPickupAssignmentDetail";
import { useLocalization } from "../../hooks/useLocalization";

const EMPTY_FORM = {
  employee_id: "", assignment_date: "", pickup_purpose: "", pickup_location: "",
  receiving_store: "", expected_start_time: "", expected_finish_time: "", fixed_assignment_amount_jpy: "6000",
};
const STATUSES = ["DRAFT", "PENDING_APPROVAL", "MIN_WAGE_ADJUSTMENT_REQUIRED", "APPROVED", "CANCELLED"];

export default function SupplyPickupAssignmentsPage() {
  const { t } = useLocalization();
  const { canViewSupplyPickup, canManageSupplyPickup } = usePermissions();
  const [assignments, setAssignments] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [status, setStatus] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [busy, setBusy] = useState(false);
  const [expandedId, setExpandedId] = useState(null);

  const load = useCallback(() => {
    if (!canViewSupplyPickup) return;
    supplyPickupApi.list({ status: status || undefined }).then((r) => setAssignments(r.data)).catch(() => {});
    apiClient.get("/employees/supply-pickup-lookup").then((r) => setDrivers(r.data)).catch(() => {});
  }, [canViewSupplyPickup, status]);
  useEffect(() => { load(); }, [load]);
  useRouteRefresh(load);

  if (!canViewSupplyPickup) {
    return <p className="text-sm text-gray-500" data-testid="supply-pickup-access-denied">{t("common.accessDenied")}</p>;
  }

  const createAssignment = async (e) => {
    e.preventDefault();
    if (!form.employee_id || !form.assignment_date || !form.pickup_purpose) return;
    setBusy(true);
    try {
      await supplyPickupApi.create(form);
      toast.success(t("mgmtSupplyPickup.assignmentCreated"));
      setForm(EMPTY_FORM);
      setShowForm(false);
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-5" data-testid="supply-pickup-assignments-page">
      <PageHelpBanner pageKey="supply-pickup-assignments" title={t("pageHelp.defaultTitle")}>
        <p dangerouslySetInnerHTML={{ __html: t("mgmtSupplyPickup.help") }} />
      </PageHelpBanner>

      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-bold tracking-tight text-gray-900">{t("mgmtSupplyPickup.title")}</h2>
          <p className="text-sm text-gray-500">{t("mgmtSupplyPickup.subtitle")}</p>
        </div>
        {canManageSupplyPickup && (
          <button data-testid="supply-pickup-new-assignment-btn" onClick={() => setShowForm((s) => !s)} className="px-4 py-2 rounded-lg text-xs font-semibold bg-blue-600 text-white hover:bg-blue-700">
            {showForm ? t("common.cancel") : `+ ${t("mgmtSupplyPickup.newAssignment")}`}
          </button>
        )}
      </div>

      {showForm && (
        <form onSubmit={createAssignment} className="bg-white rounded-xl border border-gray-200 p-5 grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="supply-pickup-new-assignment-form">
          <select data-testid="sp-employee-select" required value={form.employee_id} onChange={(e) => setForm((f) => ({ ...f, employee_id: e.target.value }))} className="border border-gray-200 rounded-lg px-3 py-2 text-sm md:col-span-2">
            <option value="">{t("mgmtSupplyPickup.selectDriverRequired")}</option>
            {drivers.map((d) => (
              <option key={d.id} value={d.id}>{d.legal_name} ({d.employee_id}){d.work_status === "INACTIVE" ? ` - ${t("mgmtSupplyPickup.inactive")}` : ""}</option>
            ))}
          </select>
          <input data-testid="sp-assignment-date-input" required type="date" value={form.assignment_date} onChange={(e) => setForm((f) => ({ ...f, assignment_date: e.target.value }))} className="border border-gray-200 rounded-lg px-3 py-2 text-sm" />
          <input data-testid="sp-fixed-amount-input" required type="number" min="0" value={form.fixed_assignment_amount_jpy} onChange={(e) => setForm((f) => ({ ...f, fixed_assignment_amount_jpy: e.target.value }))} placeholder={t("mgmtSupplyPickup.fixedAssignmentPayRequired")} className="border border-gray-200 rounded-lg px-3 py-2 text-sm" />
          <input data-testid="sp-pickup-purpose-input" required value={form.pickup_purpose} onChange={(e) => setForm((f) => ({ ...f, pickup_purpose: e.target.value }))} placeholder={t("mgmtSupplyPickup.pickupPurposeRequired")} className="border border-gray-200 rounded-lg px-3 py-2 text-sm md:col-span-2" />
          <input data-testid="sp-pickup-location-input" value={form.pickup_location} onChange={(e) => setForm((f) => ({ ...f, pickup_location: e.target.value }))} placeholder={t("mgmtSupplyPickup.pickupLocation")} className="border border-gray-200 rounded-lg px-3 py-2 text-sm" />
          <input data-testid="sp-receiving-store-input" value={form.receiving_store} onChange={(e) => setForm((f) => ({ ...f, receiving_store: e.target.value }))} placeholder={t("mgmtSupplyPickup.receivingStore")} className="border border-gray-200 rounded-lg px-3 py-2 text-sm" />
          <input data-testid="sp-expected-start-input" type="time" value={form.expected_start_time} onChange={(e) => setForm((f) => ({ ...f, expected_start_time: e.target.value }))} placeholder={t("mgmtSupplyPickup.expectedStart")} className="border border-gray-200 rounded-lg px-3 py-2 text-sm" />
          <input data-testid="sp-expected-finish-input" type="time" value={form.expected_finish_time} onChange={(e) => setForm((f) => ({ ...f, expected_finish_time: e.target.value }))} placeholder={t("mgmtSupplyPickup.expectedFinish")} className="border border-gray-200 rounded-lg px-3 py-2 text-sm" />
          <button data-testid="sp-create-assignment-btn" disabled={busy || !form.employee_id || !form.assignment_date || !form.pickup_purpose} type="submit" className="px-4 py-2 rounded-lg text-xs font-semibold bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 md:col-span-2">
            {busy ? t("mgmtHealthCommittee.creatingEllipsis") : t("mgmtSupplyPickup.createAssignment")}
          </button>
        </form>
      )}

      <div className="flex gap-2 flex-wrap">
        <button data-testid="sp-status-filter-ALL" onClick={() => setStatus("")} className={`px-2.5 py-1 rounded-md text-xs font-medium border ${!status ? "bg-gray-800 text-white border-gray-800" : "border-gray-200 bg-white hover:bg-gray-50 text-gray-600"}`}>{t("mgmtLongHourInterview.all")}</button>
        {STATUSES.map((s) => (
          <button key={s} data-testid={`sp-status-filter-${s}`} onClick={() => setStatus(s)}
            className={`px-2.5 py-1 rounded-md text-xs font-medium border ${status === s ? "bg-gray-800 text-white border-gray-800" : "border-gray-200 bg-white hover:bg-gray-50 text-gray-600"}`}>
            {s.replace(/_/g, " ")}
          </button>
        ))}
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]">
          <thead className="bg-gray-50 text-gray-500 text-left">
            <tr>
              <th className="px-4 py-3 font-medium">{t("mgmtSupplyPickup.driver")}</th><th className="px-4 py-3 font-medium">{t("common.date")}</th>
              <th className="px-4 py-3 font-medium">{t("mgmtSupplyPickup.purpose")}</th><th className="px-4 py-3 font-medium">{t("mgmtSupplyPickup.fixedPay")}</th>
              <th className="px-4 py-3 font-medium">{t("mgmtSupplyPickup.effectiveYenPerHr")}</th><th className="px-4 py-3 font-medium">{t("common.status")}</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {assignments.map((a) => {
              const driver = drivers.find((d) => d.id === a.employee_id);
              return (
                <React.Fragment key={a.id}>
                  <tr className="border-t border-gray-100" data-testid={`sp-assignment-row-${a.id}`}>
                    <td className="px-4 py-2 text-gray-700">{driver?.legal_name || a.employee_id}</td>
                    <td className="px-4 py-2 text-gray-500 text-xs">{a.assignment_date}</td>
                    <td className="px-4 py-2 text-gray-700">{a.pickup_purpose}</td>
                    <td className="px-4 py-2 text-gray-700">¥{Number(a.fixed_assignment_amount_jpy).toLocaleString()}</td>
                    <td className="px-4 py-2 text-gray-500 text-xs">{a.effective_hourly_amount_jpy ? `¥${Number(a.effective_hourly_amount_jpy).toLocaleString()}/hr` : "-"}</td>
                    <td className="px-4 py-2"><StatusBadge status={a.status} /></td>
                    <td className="px-4 py-2">
                      <button data-testid={`sp-assignment-expand-${a.id}`} onClick={() => setExpandedId((cur) => (cur === a.id ? null : a.id))} className="text-xs font-semibold text-blue-600 hover:underline">
                        {expandedId === a.id ? t("common.close") : t("mgmtSupplyPickup.manage")}
                      </button>
                    </td>
                  </tr>
                  {expandedId === a.id && (
                    <tr className="border-t border-gray-100 bg-gray-50" data-testid={`sp-assignment-detail-${a.id}`}>
                      <td colSpan={7} className="px-4 py-4">
                        <SupplyPickupAssignmentDetail assignment={a} canManage={canManageSupplyPickup} onChanged={load} />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
            {assignments.length === 0 && <tr><td colSpan={7} className="px-4 py-6 text-center text-gray-500" data-testid="no-supply-pickup-assignments">{t("mgmtSupplyPickup.noAssignmentsFilter")}</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
